import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SearchSuggestion } from '../../components/SearchForm/types';
import useSearchOrgIds, { useOrgIds } from '../../hooks/useSearchOrgIds';
import useProviderNames from '../../api/useProviderNames';
import useGetOrganizationMembers, {
    GetOrganizationMembersParams
} from './api/useGetOrganizationMembers';
import { useMemberListSnapshot } from './OrganizationMemberDetails/MemberDetailsWrapper/useMemberListNeighbors';
import {
    buildMemberSuggestionQuery,
    getMemberList,
    MEMBER_LIST_VERSIONS,
    MEMBERS_PATH,
    orderMemberLists,
    SUGGESTION_LIMIT,
    toMemberSuggestions
} from './utils/memberSuggestions';

// Suggestions are a disposable read, so a term typed again — or backspaced back to — is served
// from cache instead of refetching all three lists.
const SUGGESTION_STALE_TIME = 30_000;

// The list the user is looking at: the open tab on the members page, and on a member's detail page
// the list they opened that member from (the same snapshot the prev/next arrows walk). Check-ins
// and a missing snapshot both resolve to Active.
const useViewedMemberList = () => {
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const snapshot = useMemberListSnapshot();

    if (pathname === MEMBERS_PATH) {
        return getMemberList(searchParams.get('list_version'));
    }

    return getMemberList(
        snapshot?.listKind === 'members'
            ? (snapshot.listQuery as GetOrganizationMembersParams).list_version
            : undefined
    );
};

/**
 * Feeds member quick-results into the header search dropdown.
 *
 * [HWM-23296] The Members page splits its population across three API lists, so a term matching a
 * discharged member returned nothing while the Active tab was open. Suggestions now query all
 * three in parallel and tag each result with the list it came from. Which lists are queried never
 * depends on the open tab; only the order their results fill the dropdown does. Submitting ("Show
 * all results", or Enter with nothing highlighted) follows the top result's list.
 *
 * Adding a fourth list means a `MEMBER_LIST_VERSIONS` entry *and* a query below.
 */
const useMemberSearchSuggestions = (query: string) => {
    const orgIds = useOrgIds();
    const isMultiOrg = orgIds.length > 1;
    const providerNames = useProviderNames(orgIds);
    const viewedList = useViewedMemberList();
    // Undefined for single-org logins, which lets useRequest inject the active org as a scalar.
    const heroOrgIds = useSearchOrgIds(query);

    const options = { enabled: !!query, staleTime: SUGGESTION_STALE_TIME };

    // One call per list — hooks can't be called from a loop.
    const activeList = useGetOrganizationMembers(
        buildMemberSuggestionQuery(query, MEMBER_LIST_VERSIONS.Active, heroOrgIds),
        options
    );
    const dischargedList = useGetOrganizationMembers(
        buildMemberSuggestionQuery(query, MEMBER_LIST_VERSIONS.Discharged, heroOrgIds),
        options
    );
    const disengagedList = useGetOrganizationMembers(
        buildMemberSuggestionQuery(query, MEMBER_LIST_VERSIONS.Disengaged, heroOrgIds),
        options
    );

    // The viewed list outranks the others, so enough matches there fill the box on their own.
    // Mapped here (not via useSuggestionList) so the provider-name label recomputes when provider
    // names resolve asynchronously.
    const suggestions = useMemo<SearchSuggestion[]>(() => {
        const members = {
            Active: activeList.data?.list,
            Discharged: dischargedList.data?.list,
            Disengaged: disengagedList.data?.list
        };

        return orderMemberLists(viewedList)
            .flatMap((list) => toMemberSuggestions(members[list], list, providerNames, isMultiOrg))
            .slice(0, SUGGESTION_LIMIT);
    }, [
        viewedList,
        activeList.data,
        dischargedList.data,
        disengagedList.data,
        providerNames,
        isMultiOrg
    ]);

    return {
        suggestions,
        // All three or nothing: a late response would splice rows in above the keyboard highlight
        // and shift every index down.
        isFetching: [activeList, dischargedList, disengagedList].some(
            ({ isFetching }) => isFetching
        )
    };
};

export default useMemberSearchSuggestions;
